import React from 'react';
import { ui } from '../../config/uiSizes';
import CollapsibleFindingsSection from './CollapsibleFindingsSection';
import {
    isTargetReadinessWarning,
    stripTargetReadinessPrefix,
    isDeprecatedFlowTypeWarning,
    stripDeprecatedFlowTypePrefix,
    isDuplicateReadinessWarning,
} from '../../utils/migrationWarnings';

interface TargetReadinessPanelProps {
    /**
     * Raw job warnings from any stage (export/transform/import). Only `[TARGET-READINESS]` and
     * `[FLOW-TYPE-DEPRECATED]` lines are surfaced; everything else is ignored.
     */
    warnings?: string[];
    /** Optional extra classes for the outer container. */
    className?: string;
}

interface ReadinessItem {
    text: string;
    /** True for a platform-deprecated flow type that must be rebuilt (not just a target-org tweak). */
    deprecated: boolean;
    /** True for a duplicate target-org name (data action / script / queue) that blocks flow publish. */
    duplicate: boolean;
}

/**
 * Amber "Target org readiness" panel that surfaces the backend's pre-flight prerequisite findings so
 * the operator can act before (or between) migration runs instead of discovering a blocked publish
 * afterwards. It renders the `[TARGET-READINESS]` findings the Importer's `TargetOrgReadinessValidator`
 * emits (missing TTS voice/engine, duplicate script/queue/data action, division 50-cap, emergency-number
 * collision — F-06/F-08/F-09/F-19/F-22) and the Exporter's `[FLOW-TYPE-DEPRECATED]` warning (F-28), each
 * with the concrete Genesys admin remediation already embedded in the message. Renders nothing when
 * there are no readiness findings.
 */
const TargetReadinessPanel: React.FC<TargetReadinessPanelProps> = ({ warnings, className = '' }) => {
    const items: ReadinessItem[] = (warnings ?? [])
        .filter((w) => isTargetReadinessWarning(w) || isDeprecatedFlowTypeWarning(w))
        .map((w) =>
            isDeprecatedFlowTypeWarning(w)
                ? { text: stripDeprecatedFlowTypePrefix(w), deprecated: true, duplicate: false }
                : {
                    text: stripTargetReadinessPrefix(w),
                    deprecated: false,
                    duplicate: isDuplicateReadinessWarning(w),
                },
        );

    if (items.length === 0) {
        return null;
    }

    const duplicateCount = items.filter((i) => i.duplicate).length;

    return (
        <CollapsibleFindingsSection
            variant="amber"
            title="Target org readiness"
            iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            count={items.length}
            sectionId="target-readiness"
            defaultOpen={duplicateCount > 0}
            className={className}
            criticalBadge={
                duplicateCount > 0 ? (
                    <span
                        className={`inline-flex items-center ${ui.badge.padding} rounded-full ${ui.text.badge} font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200`}
                    >
                        {duplicateCount} duplicate{duplicateCount === 1 ? '' : 's'}
                    </span>
                ) : undefined
            }
            summary="Prerequisites to resolve in the target org so affected resources can fully land and publish."
        >
            <p className={`${ui.text.xs} text-amber-800 dark:text-amber-200 ${ui.mb.sm} break-words`}>
                Address these prerequisites in the target organization so the affected resources can fully
                land and publish, then re-run or re-publish the migration.
            </p>

            {duplicateCount > 0 && (
                <div
                    className={`bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-md ${ui.page.smPadding} ${ui.mb.sm}`}
                >
                    <p className={`${ui.text.xs} font-semibold text-red-800 dark:text-red-200 break-words`}>
                        {duplicateCount} duplicate target-org resource name{duplicateCount === 1 ? '' : 's'} can
                        block flow publishing. Delete or rename the duplicate(s) so exactly one of each name
                        remains, then re-run the migration.
                    </p>
                </div>
            )}

            <ul className={`flex flex-col ${ui.gap.xs}`}>
                {items.map((item, index) => (
                    <li
                        key={index}
                        className={`flex items-start ${ui.gap.xs} ${ui.text.xs} text-amber-700 dark:text-amber-300`}
                    >
                        <span className="text-amber-500 dark:text-amber-400 shrink-0" aria-hidden="true">
                            •
                        </span>
                        <span className="break-words">
                            {item.deprecated && (
                                <span className={`${ui.text.xxs} font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mr-1`}>
                                    Deprecated flow type:
                                </span>
                            )}
                            {item.duplicate && (
                                <span className={`${ui.text.xxs} font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 mr-1`}>
                                    Duplicate name:
                                </span>
                            )}
                            {item.text}
                        </span>
                    </li>
                ))}
            </ul>
        </CollapsibleFindingsSection>
    );
};

export default TargetReadinessPanel;
